import { useCallback, useMemo, useState } from 'react';
import { t, Trans } from '@lingui/macro';

import SearchInput from '@decisiv/ui-components/lib/components/SearchInput';
import { P } from '@decisiv/ui-components/lib/components/Typography';

import Loading from 'components/Loading';
import useSort from 'utils/useSort';

import { useCurrentUserContext } from 'utils/User';
import * as ManagementLayout from 'components/ManagementLayout';
import PaginationFooter from 'components/PaginationFooter';
import SearchableFilter from 'components/SearchableFilter';
import TeamModalProvider from 'pages/Teams/CreateTeamModal/contexts/TeamModalContext';
import CreateTeamModal from 'pages/Teams/CreateTeamModal';
import useFooter from 'utils/useFooter';
import { NoWrapHeaderTable } from '../components/styles';
import { useManagedSentinelTeamsQuery } from './hooks/useManagedSentinelTeamsQuery';

import { useManagementPageContext } from '../contexts/ManagementPageContext';
import useOemSegmentsFilters from '../OemSegments/hooks/useOemSegmentsFilters';
import useTeamsFilters from './hooks/useTeamsFilters';
import { generateColumns } from './columns';

export type ManagedTeamsType = ExtractFromArray<
  ManagedSentinelTeamsQuery['managedSentinelTeams']['collection']
>;

const Teams = () => {
  const { footerRef } = useFooter();
  const { isSuperUser } = useCurrentUserContext();

  const { pageParams, setPageParams } = useManagementPageContext();

  const [page, setPage] = useState(1);

  const [{ sort }, { getSortProps }] = useSort();
  const { defaultSort, onSort } = getSortProps();

  const { data, loading } = useManagedSentinelTeamsQuery({
    variables: {
      sort,
      page,
      ids: pageParams.teamIds,
      oemSegmentIds: pageParams.oemSegmentIds,
    },
  });

  const handleOnSubmitOemSegments = useCallback(
    ({ value }) =>
      setPageParams((oldState) => ({
        ...oldState,
        oemSegmentIds: value as string[],
      })),
    [setPageParams],
  );

  const handleOnSubmitTeams = useCallback(
    ({ value }) =>
      setPageParams((oldState) => ({
        ...oldState,
        teamIds: value as string[],
      })),
    [setPageParams],
  );

  const oemSegmentsFilterProps = useOemSegmentsFilters({
    value: pageParams.oemSegmentIds,
    onSubmit: handleOnSubmitOemSegments,
  });

  const teamFilterProps = useTeamsFilters({
    value: pageParams.teamIds,
    onSubmit: handleOnSubmitTeams,
  });

  const columns = useMemo(() => generateColumns({ onSort }), [onSort]);

  const teams = data?.managedSentinelTeams?.collection ?? [];
  const pagination = data?.managedSentinelTeams?.pagination;

  return (
    <ManagementLayout.TabBody.Container>
      <ManagementLayout.TabBody.Header.Container>
        <ManagementLayout.TabBody.Header.Left>
          {isSuperUser && (
            <TeamModalProvider>
              <CreateTeamModal />
            </TeamModalProvider>
          )}
        </ManagementLayout.TabBody.Header.Left>
        <ManagementLayout.TabBody.Header.Right>
          <P size="small" marginRight={1}>
            <Trans>Search Teams by Name:</Trans>
          </P>
          <SearchInput value="" hideLabel name="searchByTeam" size="small" />
        </ManagementLayout.TabBody.Header.Right>
      </ManagementLayout.TabBody.Header.Container>
      <ManagementLayout.TabBody.Content.Container>
        <ManagementLayout.TabBody.Content.Filters>
          <SearchableFilter
            popoverLabel={t`OEM Segments`}
            placement="bottom-start"
            {...oemSegmentsFilterProps}
          />
          <SearchableFilter popoverLabel={t`Teams`} {...teamFilterProps} />
        </ManagementLayout.TabBody.Content.Filters>
        {loading && <Loading />}
        {!loading && (
          <NoWrapHeaderTable
            height="100%"
            data={teams}
            scroll={['x', 'y']}
            columns={columns}
            onSort={onSort}
            defaultSort={defaultSort}
            footer={() => (
              <PaginationFooter
                ref={footerRef}
                // @ts-expect-error - JS file
                title={t`Teams`}
                setCurrentPage={setPage}
                totalOnPage={teams?.length}
                page={pagination?.page}
                perPage={pagination?.perPage}
                totalEntries={pagination?.totalEntries}
                totalPages={pagination?.totalPages}
              />
            )}
          />
        )}
      </ManagementLayout.TabBody.Content.Container>
    </ManagementLayout.TabBody.Container>
  );
};

export default Teams;
